import { ComponentProps, useMemo } from 'react';
import { Box, FormControl, FormHelperText, FormLabel, MenuItem, Paper, Select, SelectChangeEvent, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { SchemaFormArray, useFormHellFieldValidationMessages } from 'formhell';
import { useSchemaFormContext } from '../SchemaFormContext';

type SchemaFormArrayProps = ComponentProps<typeof SchemaFormArray>;

const NONE_SENTINEL = '__null__';
const FIXED_LENGTH = 2;
const THUMB_SIZE = 40;
const SLOT_LABELS = ['Primary Image', 'Secondary Image'];

const normalizeValue = (value: unknown): (string | null)[] => {
    const arr = Array.isArray(value) ? [...value] : [];
    while (arr.length < FIXED_LENGTH) {
        arr.push(null);
    }
    return arr.slice(0, FIXED_LENGTH);
};

const ImageThumb = ({ src, size = THUMB_SIZE, empty = false }: { src?: string; size?: number; empty?: boolean }) => {
    if (!src) {
        return (
            <Box sx={{
                width: size, height: size, borderRadius: 1,
                border: empty ? '1.5px dashed' : 'none',
                borderColor: 'grey.300',
                backgroundColor: 'grey.100', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <ImageIcon sx={{ fontSize: size * 0.5, color: 'grey.400' }} />
            </Box>
        );
    }
    return (
        <Box sx={{
            width: size, height: size, borderRadius: 1,
            border: '1px solid', borderColor: 'divider',
            overflow: 'hidden', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'grey.50',
        }}>
            <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </Box>
    );
};

export const AppImageSelectArray = ({ label, required, pointer, value, disabled, onChange, itemsSchema, itemSchemas }: SchemaFormArrayProps) => {
    const id = `root${pointer.replace(/\//g, '_')}`;
    const errors = useFormHellFieldValidationMessages(pointer);
    const { imagePreviewMap, readOnly } = useSchemaFormContext();
    const isDisabled = !!disabled || readOnly;
    const items = useMemo(() => normalizeValue(value), [value]);

    const options = useMemo(() => {
        const enumValues: unknown[] = (itemSchemas?.[0]?.enum ?? itemsSchema?.enum ?? []) as unknown[];
        return enumValues
            .filter(entry => entry !== null && entry !== undefined && entry !== '')
            .map(entry => String(entry));
    }, [itemSchemas, itemsSchema]);

    const handleChange = (index: number) => (event: SelectChangeEvent<string>) => {
        const next = [...items];
        next[index] = event.target.value === NONE_SENTINEL ? null : event.target.value;
        onChange(next);
    };

    return (
        <div className="raf-field">
            <FormLabel htmlFor={id} required={required}>{label}</FormLabel>
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                    {items.map((item, index) => {
                        const selectedPreview = item ? imagePreviewMap[item] : undefined;
                        return (
                            <FormControl key={`${id}_${index}`} sx={{ minWidth: 220 }} size="medium" disabled={isDisabled} error={errors.length > 0}>
                                <FormLabel htmlFor={`${id}_${index}`} sx={{ fontWeight: 500, fontSize: '0.8rem', mb: 0.5, color: 'text.secondary' }}>
                                    {SLOT_LABELS[index] ?? `Image ${index + 1}`}
                                </FormLabel>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {selectedPreview && <ImageThumb src={selectedPreview} />}
                                    <Select
                                        id={`${id}_${index}`}
                                        value={item === null || item === undefined ? NONE_SENTINEL : item}
                                        onChange={handleChange(index)}
                                        displayEmpty
                                        sx={{ flex: 1 }}
                                        renderValue={(selected) => (
                                            selected === NONE_SENTINEL
                                                ? <Typography variant="body2" sx={{ color: 'text.disabled' }}>Click to select</Typography>
                                                : <Typography variant="body2">{String(selected)}</Typography>
                                        )}
                                    >
                                        <MenuItem value={NONE_SENTINEL}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ImageThumb size={28} />
                                                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>None</Typography>
                                            </Box>
                                        </MenuItem>
                                        {options.map(option => (
                                            <MenuItem key={option} value={option}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <ImageThumb src={imagePreviewMap[option]} size={28} />
                                                    <Typography variant="body2">{option}</Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            </FormControl>
                        );
                    })}
                </Box>
                {errors.length > 0 && <FormHelperText error>{errors.join(', ')}</FormHelperText>}
            </Paper>
        </div>
    );
};
